// CR080: Render PDFs Dialog - PDF→images ONLY, NO AI extraction
// Phase 0: This dialog ONLY renders PDFs to images, does NOT trigger any AI
// Use separate "Run OCR + Candidate Scan" and "Run AI Deep Scan" actions after rendering
import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle, XCircle, ImageIcon, FileText, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// CR086: Static import of PDF.js with CDN worker pre-configured
import { getPdfjs } from "@/lib/pdfjs-config";

interface StagingItem {
  id: string;
  batch_run_id: string;
  merchant_key: string;
  evidence_json: {
    // CR168: Unified bucket/key schema for PDF location
    flyer_bucket?: string;        // Storage bucket name
    flyer_storage_key?: string;   // Storage object key
    // Legacy field (deprecated)
    flyer_storage_path?: string;
    flyer_filename?: string;
    render_status?: string;
    flyer_pages?: Array<{ page_number: number; storage_path: string; width: number; height: number }>;
    [key: string]: unknown;
  };
}

interface FlyerProgress {
  id: string;
  merchantKey: string;
  filename: string;
  status: "pending" | "rendering" | "success" | "error";
  pageCount?: number;
  error?: string;
}

interface RenderAllAndContinueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchRunId: string;
  stepId: string;
  pendingFlyers: StagingItem[];
  onComplete: () => void;
}

// CR080: Removed "extracting" phase - this dialog is render-only now
type Phase = "ready" | "rendering" | "complete" | "error";

export function RenderAllAndContinueDialog({
  open,
  onOpenChange,
  batchRunId,
  stepId,
  pendingFlyers,
  onComplete,
}: RenderAllAndContinueDialogProps) {
  const { toast } = useToast();
  const [phase, setPhase] = useState<Phase>("ready");
  const [flyerProgress, setFlyerProgress] = useState<FlyerProgress[]>([]);
  const [overallProgress, setOverallProgress] = useState(0);
  const [renderResult, setRenderResult] = useState<{
    successCount: number;
    totalPages: number;
    failedCount: number;
  } | null>(null);

  // Initialize flyer progress when dialog opens
  useEffect(() => {
    if (open && pendingFlyers.length > 0) {
      setFlyerProgress(
        pendingFlyers.map((f) => ({
          id: f.id,
          merchantKey: f.merchant_key,
          filename: f.evidence_json?.flyer_filename || f.evidence_json?.flyer_storage_path?.split("/").pop() || "Unknown",
          status: "pending",
        }))
      );
      setPhase("ready");
      setOverallProgress(0);
      setRenderResult(null);
    }
  }, [open, pendingFlyers]);

  const updateFlyerStatus = useCallback(
    (id: string, update: Partial<FlyerProgress>) => {
      setFlyerProgress((prev) =>
        prev.map((f) => (f.id === id ? { ...f, ...update } : f))
      );
    },
    []
  );

  // Render a single PDF to page images
  const renderSingleFlyer = useCallback(
    async (item: StagingItem): Promise<{ success: boolean; pageCount: number; error?: string }> => {
      const evidence = item.evidence_json;
      
      // CR168: Use explicit bucket/key if available, fallback to legacy path
      const bucketName = evidence?.flyer_bucket || "flyer-fetch-assets";
      const pdfPathRaw = evidence?.flyer_storage_key || evidence?.flyer_storage_path;

      console.log(`[RenderFlyer] Starting render for item ${item.id}, bucket: ${bucketName}, key: ${pdfPathRaw}`);

      if (!pdfPathRaw) {
        console.error(`[RenderFlyer] No PDF storage key for item ${item.id}`);
        return { success: false, pageCount: 0, error: "No PDF storage key" };
      }

      // Normalize path
      let pdfPath = pdfPathRaw;
      const publicUrlMatch = pdfPathRaw.match(/\/storage\/v1\/object\/(?:public|sign)\/[^/]+\/(.+)/);
      if (publicUrlMatch) {
        pdfPath = decodeURIComponent(publicUrlMatch[1]);
      }
      if (pdfPath.startsWith("/")) {
        pdfPath = pdfPath.slice(1);
      }
      
      console.log(`[RenderFlyer] Normalized path: ${pdfPath}`);

      try {
        // Check if file exists
        const pathParts = pdfPath.split("/");
        const fileName = pathParts.pop() || "";
        const folderPath = pathParts.join("/");

        console.log(`[RenderFlyer] Checking storage folder: ${folderPath}, file: ${fileName}`);

        const { data: listData, error: listError } = await supabase.storage
          .from(bucketName)
          .list(folderPath);

        if (listError) {
          console.error(`[RenderFlyer] Storage list error:`, listError);
          return { success: false, pageCount: 0, error: `Storage access failed: ${listError.message}` };
        }

        const fileExists = listData?.some((f) => f.name === fileName);
        if (!fileExists) {
          console.error(`[RenderFlyer] PDF not found. Folder contents:`, listData?.map(f => f.name));
          return { success: false, pageCount: 0, error: `PDF not found in storage at ${pdfPath}` };
        }

        console.log(`[RenderFlyer] PDF exists, creating signed URL...`);

        // CR083: Get signed URL with longer expiration (15 min) for large PDFs
        const { data: signedUrlData, error: signedUrlError } = await supabase.storage
          .from(bucketName)
          .createSignedUrl(pdfPath, 900); // 15 minutes

        if (signedUrlError || !signedUrlData?.signedUrl) {
          console.error(`[RenderFlyer] Signed URL error:`, signedUrlError);
          return { success: false, pageCount: 0, error: `Signed URL failed: ${signedUrlError?.message}` };
        }

        console.log(`[RenderFlyer] Signed URL created, running preflight...`);

        // CR083: Preflight check - verify PDF is accessible before rendering
        let preflightResponse: Response;
        try {
          preflightResponse = await fetch(signedUrlData.signedUrl, { method: "HEAD" });
        } catch (preflightErr) {
          console.error(`[RenderFlyer] Preflight fetch error:`, preflightErr);
          return { success: false, pageCount: 0, error: `Preflight network error: ${preflightErr instanceof Error ? preflightErr.message : "Unknown"}` };
        }
        
        if (!preflightResponse.ok) {
          let errorDetail = `HTTP ${preflightResponse.status}`;
          if (preflightResponse.status === 403) errorDetail = "Access denied (403) - URL may have expired";
          else if (preflightResponse.status === 404) errorDetail = "File not found (404) - may have been deleted";
          else if (preflightResponse.status === 401) errorDetail = "Auth failed (401)";
          console.error(`[RenderFlyer] Preflight failed:`, errorDetail);
          return { success: false, pageCount: 0, error: `Preflight failed: ${errorDetail}` };
        }

        console.log(`[RenderFlyer] Preflight passed, fetching PDF bytes...`);

        // Fetch PDF
        let response: Response;
        try {
          response = await fetch(signedUrlData.signedUrl);
        } catch (fetchErr) {
          console.error(`[RenderFlyer] PDF fetch network error:`, fetchErr);
          return { success: false, pageCount: 0, error: `PDF fetch network error: ${fetchErr instanceof Error ? fetchErr.message : "Unknown"}` };
        }
        
        if (!response.ok) {
          console.error(`[RenderFlyer] PDF fetch failed with status ${response.status}`);
          return { success: false, pageCount: 0, error: `PDF fetch failed: ${response.status}` };
        }

        const pdfBytes = await response.arrayBuffer();
        console.log(`[RenderFlyer] PDF fetched, ${pdfBytes.byteLength} bytes. Parsing with PDF.js...`);

        let pdf: Awaited<ReturnType<Awaited<ReturnType<typeof getPdfjs>>["getDocument"]>["promise"]>;
        try {
          const pdfjsLib = await getPdfjs();
          const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
          pdf = await loadingTask.promise;
          console.log(`[RenderFlyer] PDF parsed, ${pdf.numPages} pages`);
        } catch (parseErr) {
          console.error(`[RenderFlyer] PDF parse error:`, parseErr);
          return { success: false, pageCount: 0, error: `PDF parse failed: ${parseErr instanceof Error ? parseErr.message : "Unknown"}` };
        }
        const pageCount = pdf.numPages;

        const uploadedPages: Array<{ page_number: number; storage_path: string; width: number; height: number }> = [];
        const renderErrors: string[] = [];

        for (let i = 1; i <= pageCount; i++) {
          try {
            console.log(`[RenderFlyer] Rendering page ${i}/${pageCount}...`);
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale: 2.0 });

            const canvas = document.createElement("canvas");
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const ctx = canvas.getContext("2d");

            if (!ctx) {
              console.error(`[RenderFlyer] Page ${i}: Failed to get canvas context`);
              renderErrors.push(`Page ${i}: No canvas context`);
              continue;
            }

            await page.render({ canvasContext: ctx, viewport }).promise;

            const blob = await new Promise<Blob | null>((resolve) => {
              canvas.toBlob(resolve, "image/jpeg", 0.85);
            });

            if (!blob) {
              console.error(`[RenderFlyer] Page ${i}: Failed to create blob`);
              renderErrors.push(`Page ${i}: Blob creation failed`);
              continue;
            }

            const storagePath = `rendered/${batchRunId}/${item.id}/page_${i}.jpg`;

            const { error: uploadError } = await supabase.storage
              .from("flyer-fetch-assets")
              .upload(storagePath, blob, {
                contentType: "image/jpeg",
                upsert: true,
              });

            if (uploadError) {
              console.error(`[RenderFlyer] Page ${i}: Upload failed:`, uploadError);
              renderErrors.push(`Page ${i}: Upload failed - ${uploadError.message}`);
              continue;
            }

            console.log(`[RenderFlyer] Page ${i}: Uploaded to ${storagePath}`);
            uploadedPages.push({
              page_number: i,
              storage_path: storagePath,
              width: viewport.width,
              height: viewport.height,
            });
          } catch (pageErr) {
            console.error(`[RenderFlyer] Page ${i}: Render error:`, pageErr);
            renderErrors.push(`Page ${i}: ${pageErr instanceof Error ? pageErr.message : "Unknown error"}`);
          }
        }

        if (uploadedPages.length === 0) {
          const errorMsg = renderErrors.length > 0 
            ? `Failed to render/upload pages: ${renderErrors.slice(0, 3).join("; ")}` 
            : "Failed to upload any pages";
          console.error(`[RenderFlyer] All pages failed:`, renderErrors);
          return { success: false, pageCount: 0, error: errorMsg };
        }

        console.log(`[RenderFlyer] Successfully rendered ${uploadedPages.length}/${pageCount} pages`);

        // Update staging row - CR080: Only update render status, NOT trigger any extraction
        const currentEvidence = item.evidence_json || {};
        const { error: updateError } = await supabase
          .from("cr075_staging_deal_items")
          .update({
            evidence_json: {
              ...currentEvidence,
              flyer_pages: uploadedPages,
              render_status: "RENDERED",
              page_count: uploadedPages.length,
              rendered_at: new Date().toISOString(),
              // CR080: Mark that OCR scan is needed next
              ocr_scan_status: "NOT_STARTED",
            },
          })
          .eq("id", item.id);

        if (updateError) {
          return { success: false, pageCount: uploadedPages.length, error: `DB update failed: ${updateError.message}` };
        }

        // CR456: Persist truth rows via edge function (RLS-safe, service role)
        try {
          const { error: truthErr } = await supabase.functions.invoke(
            "persist-flyer-rendered-pages",
            {
              body: {
                batch_run_id: batchRunId,
                flyer_merchant: item.merchant_key,
                render_source: "CLIENT_RENDER",
                pages: uploadedPages.map((p: any) => ({
                  page_number: p.page_number,
                  storage_path: p.storage_path,
                  width: p.width,
                  height: p.height,
                })),
              },
            }
          );
          if (truthErr) {
            console.error("[RenderAll] CR456: truth persistence failed:", truthErr);
          } else {
            console.log(`[RenderAll] CR456: Persisted ${uploadedPages.length} truth rows for ${item.merchant_key}`);
          }
        } catch (truthCallErr) {
          console.error("[RenderAll] CR456: truth edge function call error:", truthCallErr);
        }

        return { success: true, pageCount: uploadedPages.length };
      } catch (err) {
        return { success: false, pageCount: 0, error: err instanceof Error ? err.message : "Unknown error" };
      }
    },
    [batchRunId]
  );

  // CR080: Main orchestration - Render ONLY, no AI extraction
  const handleRenderOnly = useCallback(async () => {
    setPhase("rendering");
    let successCount = 0;
    let totalPages = 0;
    let failedCount = 0;

    // Render each flyer sequentially (to avoid memory issues with large PDFs)
    for (let i = 0; i < pendingFlyers.length; i++) {
      const flyer = pendingFlyers[i];
      updateFlyerStatus(flyer.id, { status: "rendering" });

      const result = await renderSingleFlyer(flyer);

      if (result.success) {
        updateFlyerStatus(flyer.id, { status: "success", pageCount: result.pageCount });
        successCount++;
        totalPages += result.pageCount;
      } else {
        updateFlyerStatus(flyer.id, { status: "error", error: result.error });
        failedCount++;
      }

      // Update overall progress
      setOverallProgress(Math.round(((i + 1) / pendingFlyers.length) * 100));
    }

    setRenderResult({ successCount, totalPages, failedCount });

    if (successCount === 0) {
      setPhase("error");
      toast({
        title: "Render Failed",
        description: "Failed to render any PDFs. Check the errors below.",
        variant: "destructive",
      });
      return;
    }

    // CR080: Rendering complete - NO AI extraction triggered
    setPhase("complete");

    toast({
      title: "Rendering Complete",
      description: `Rendered ${totalPages} pages from ${successCount} PDFs. Run "OCR + Candidate Scan" next.`,
    });
  }, [pendingFlyers, renderSingleFlyer, updateFlyerStatus, toast]);

  const handleClose = useCallback(() => {
    if (phase === "complete" || phase === "error") {
      onComplete();
    }
    onOpenChange(false);
  }, [phase, onComplete, onOpenChange]);

  const renderPhaseLabel = () => {
    switch (phase) {
      case "ready":
        return "Ready to render PDFs";
      case "rendering":
        return "Rendering PDF pages...";
      case "complete":
        return "Rendering complete!";
      case "error":
        return "Rendering encountered errors";
    }
  };

  const successfulRenders = flyerProgress.filter((f) => f.status === "success").length;
  const failedRenders = flyerProgress.filter((f) => f.status === "error").length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Render PDFs
          </DialogTitle>
          <DialogDescription>
            Render {pendingFlyers.length} PDFs to page images (no AI extraction)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Overall progress */}
          {phase !== "ready" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>{renderPhaseLabel()}</span>
                <span className="text-muted-foreground">{overallProgress}%</span>
              </div>
              <Progress value={overallProgress} className="h-2" />
            </div>
          )}

          {/* Stats summary */}
          {phase !== "ready" && (
            <div className="flex items-center gap-3">
              {successfulRenders > 0 && (
                <Badge variant="outline" className="bg-primary/10 text-primary gap-1">
                  <CheckCircle className="h-3 w-3" />
                  {successfulRenders} rendered
                </Badge>
              )}
              {failedRenders > 0 && (
                <Badge variant="destructive" className="gap-1">
                  <XCircle className="h-3 w-3" />
                  {failedRenders} failed
                </Badge>
              )}
              {renderResult && (
                <Badge variant="secondary" className="gap-1">
                  <FileText className="h-3 w-3" />
                  {renderResult.totalPages} pages total
                </Badge>
              )}
            </div>
          )}

          {/* Flyer list */}
          <ScrollArea className="h-48 border rounded-md p-2">
            <div className="space-y-2">
              {flyerProgress.map((flyer) => (
                <div
                  key={flyer.id}
                  className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {flyer.status === "pending" && <FileText className="h-4 w-4 text-muted-foreground shrink-0" />}
                    {flyer.status === "rendering" && <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />}
                    {flyer.status === "success" && <CheckCircle className="h-4 w-4 text-primary shrink-0" />}
                    {flyer.status === "error" && <XCircle className="h-4 w-4 text-destructive shrink-0" />}
                    <span className="truncate">{flyer.merchantKey}</span>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0">
                    {flyer.status === "success" && flyer.pageCount && `${flyer.pageCount} pages`}
                    {flyer.status === "error" && (
                      <span className="text-destructive truncate max-w-32" title={flyer.error}>
                        {flyer.error?.substring(0, 30)}...
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Success message - CR080: Updated to show next steps */}
          {phase === "complete" && renderResult && (
            <div className="bg-primary/10 text-primary p-3 rounded-md text-sm">
              <div className="flex items-center gap-2 font-medium">
                <CheckCircle className="h-4 w-4" />
                Rendering Complete
              </div>
              <p className="text-xs mt-1">
                Rendered {renderResult.totalPages} pages from {renderResult.successCount} PDFs.
                {renderResult.failedCount > 0 && ` ${renderResult.failedCount} PDF(s) failed.`}
              </p>
              <p className="text-xs mt-2 font-medium">
                Next: Click "Run OCR + Candidate Scan" to identify deal candidates.
              </p>
            </div>
          )}

          {/* Error message */}
          {phase === "error" && (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
              <div className="flex items-center gap-2 font-medium">
                <AlertCircle className="h-4 w-4" />
                Rendering Failed
              </div>
              <p className="text-xs mt-1">Failed to render PDFs. Check individual errors above.</p>
            </div>
          )}
        </div>

        <DialogFooter>
          {phase === "ready" && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleRenderOnly}>
                <ImageIcon className="h-4 w-4 mr-2" />
                Render PDFs
              </Button>
            </>
          )}
          {phase === "rendering" && (
            <Button disabled>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Rendering...
            </Button>
          )}
          {(phase === "complete" || phase === "error") && (
            <Button onClick={handleClose}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
